import React, {FC, useCallback} from "react";
import {useTranslation} from "react-i18next";
import {EnumDropdownElement, RichTextElement, TextFieldElement} from "@ib-tehnologije/react-widgets";
import {RenderAntdGraphQLList} from "../../../Components/AdminModalForm";
import {Panel} from "../../../Components/ui";
import {
    EventEmailTemplateCreateDtoInput,
    LanguageCode,
    useCreateEventEmailTemplateMutation,
    useEventEmailTemplatesListQuery,
    useUpdateEventEmailTemplateMutation
} from "../../../GraphQL/generated/graphql";
import {humanizeEnumValue} from "../../../i18n/labelHelpers";
import {parseEventEmailTemplateBody, serializeEventEmailTemplateBody} from "../eventEmailTemplateBody";
import {EventConfigScope} from "./useEventConfigScope";

const emailTemplateFormDefinition = () => ({
    language: {translationKey: "language", type: new EnumDropdownElement({enumData: LanguageCode, showCancel: false})},
    subject: {translationKey: "subject", type: new TextFieldElement({})},
    header: {translationKey: "email-template-header", type: new RichTextElement({})},
    footer: {translationKey: "email-template-footer", type: new RichTextElement({})}
});

export const EventEmailTemplatesPanel: FC<{scope: EventConfigScope}> = ({scope: {eventNumericId, baseWhere}}) => {
    const {t} = useTranslation();
    const emailTemplatesQuery = useEventEmailTemplatesListQuery({variables: {take: 50, where: baseWhere()}});
    const [createEmailTemplate] = useCreateEventEmailTemplateMutation();
    const [updateEmailTemplate] = useUpdateEventEmailTemplateMutation();
    const enumLabel = useCallback((value?: string | null) =>
        value ? t(`enums:${value}`, {defaultValue: humanizeEnumValue(value)}) : "-", [t]);

    return (
        <Panel title={t("event-config-participant-emails", {defaultValue: "Participant emails"})}>
            <RenderAntdGraphQLList
                tableData={emailTemplatesQuery.data?.eventEmailTemplates}
                columns={[
                    {columnId: "language", title: t("language"), sorter: true, renderCell: r => enumLabel(r.language)},
                    {columnId: "subject", title: t("subject"), sorter: true, renderCell: r => r.subject}
                ]}
                creating={{
                    fn: (options: any) => createEmailTemplate(options as any),
                    modelKey: "input",
                    baseFormData: {eventId: eventNumericId} as EventEmailTemplateCreateDtoInput,
                    formDefinition: emailTemplateFormDefinition(),
                    formDataToDto: values => ({
                        eventId: Number(eventNumericId ?? values.eventId),
                        language: values.language as LanguageCode,
                        subject: values.subject,
                        body: serializeEventEmailTemplateBody({
                            header: values.header,
                            footer: values.footer
                        })
                    })
                }}
                updating={{
                    fn: (options: any) => updateEmailTemplate(options as any),
                    modelKey: "input",
                    formDefinition: emailTemplateFormDefinition(),
                    formDataToDto: values => ({
                        id: Number(values.id),
                        eventId: Number(eventNumericId ?? values.eventId),
                        language: values.language as LanguageCode,
                        subject: values.subject,
                        body: serializeEventEmailTemplateBody({
                            header: values.header,
                            footer: values.footer
                        })
                    }),
                    selectionToBaseFormData: item => {
                        const bodyParts = parseEventEmailTemplateBody(item?.body);

                        return {
                            id: item?.id,
                            eventId: item?.eventId,
                            language: item?.language,
                            subject: item?.subject,
                            header: bodyParts.header,
                            footer: bodyParts.footer
                        };
                    }
                }}
                loading={emailTemplatesQuery.loading}
                getRowId={row => row.id}
                getNotificationKey={row => `${row.subject} ${row.language}`}
                query={emailTemplatesQuery}
                pageStorageKey={"event-email-templates"}
            />
        </Panel>
    );
};
